import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Upload, Loader2, Globe, EyeOff, ExternalLink } from "lucide-react";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PreviewStatsCard } from "./PreviewStatsCard";
import { WatermarkCheckCard } from "./WatermarkCheckCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminPreviewPage() {
  const { token } = useAdminAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/preview-page`, { headers }).then((r) => setCfg(r.data)).catch(() => toast.error("Could not load preview settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async (body, msg) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/preview-page`, body, { headers });
      setCfg(r.data);
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/preview-page/sheet`, fd, { headers });
      setCfg(r.data);
      toast.success(r.data.sheet?.page_count > 1 ? "Uploaded. Only page 1 is shown on the page." : "Lead Sheetz uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-preview-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Eye className="h-8 w-8 text-gold" />
          <h1 className="font-serif text-2xl font-bold">Public Preview Page</h1>
        </div>
        <Button variant="outline" onClick={() => window.open("/preview", "_blank")} data-testid="admin-preview-open-btn">
          Review /preview <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg">{cfg.published ? <Globe className="h-5 w-5 text-green-600" /> : <EyeOff className="h-5 w-5 text-amber-600" />}Visibility</CardTitle></CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground" data-testid="admin-preview-status">
            {cfg.published ? "Live: anyone can open barsongz.com/preview." : "Hidden: only admins signed in to this browser can see it."}
          </p>
          <Switch checked={cfg.published} disabled={busy} onCheckedChange={(v) => save({ published: v }, v ? "Preview page published" : "Preview page hidden")} data-testid="admin-preview-publish-switch" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">1. Sample song</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Select value={cfg.content_id || ""} onValueChange={(v) => save({ content_id: v }, "Sample song saved")} disabled={busy}>
            <SelectTrigger data-testid="admin-preview-song-select"><SelectValue placeholder="Choose an uploaded song" /></SelectTrigger>
            <SelectContent>
              {cfg.songs.map((s) => (
                <SelectItem key={s.content_id} value={s.content_id} data-testid={`admin-preview-song-${s.content_id}`}>
                  {s.course_name} · {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Only real uploaded songs are listed. Placeholders can't be used.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">2. Legal Lead Sheetz™ PDF</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="admin-preview-pdf-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="admin-preview-pdf-upload-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {cfg.sheet ? "Replace PDF" : "Upload PDF"}
          </Button>
          {cfg.sheet ? (
            <div className="space-y-2" data-testid="admin-preview-sheet-info">
              <p className="text-sm">{cfg.sheet.filename} · uploaded {new Date(cfg.sheet.uploaded_at).toLocaleString()}</p>
              <img src={`${BACKEND}${cfg.sheet.image_url}`} alt="Lead Sheetz preview" className="max-h-96 rounded-lg border" data-testid="admin-preview-sheet-thumb" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">One-page PDF, up to 10 MB. Every download is stamped with barsongz.com and a unique tracking ID.</p>
          )}
        </CardContent>
      </Card>

      <WatermarkCheckCard token={token} />

      <PreviewStatsCard />
    </div>
  );
}
